import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Header.css';
import logo from '../assets/img/logo.jpg';
import deutsch from '../assets/img/deutsch.png';
import english from '../assets/img/english.jpg';
import brochurePdf from '../assets/absorex-brochure.pdf';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  const languageDropdownRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: english },
    { code: 'de', name: 'Deutsch', flag: deutsch }
  ];

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === i18n.language) || languages[0];
  };

  const getCurrentLangPrefix = () => {
    const lang = i18n.language;
    return lang === 'en' ? '' : `/${lang}`;
  };

  const handleLanguageChange = (lang) => {
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.startsWith('/de') ? currentPath.substring(3) : currentPath;
    const newPath = lang === 'en' ? pathWithoutLang || '/' : `/${lang}${pathWithoutLang || '/'}`;
    i18n.changeLanguage(lang);
    navigate(newPath);
    setIsLanguageDropdownOpen(false);
  };

  const handleBrochureDownload = () => {
    const link = document.createElement('a');
    link.href = brochurePdf;
    link.download = 'absorex-brochure.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen(!isMobileNavOpen);
  };

  const handleMobileLinkClick = () => {
    scrollToTop();
    setIsMobileNavOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.classList.add('mobile-nav-active');
    } else {
      document.body.classList.remove('mobile-nav-active');
    }
    return () => document.body.classList.remove('mobile-nav-active');
  }, [isMobileNavOpen]);

  const productLinks = [
    { name: "ABSOREX-PACK", path: "/products/absorex-pack" },
    { name: "ABSOREX-STRIPS", path: "/products/absorex-strips" },
    { name: "ABSOREX-V-BOX", path: "/products/absorex-v-box" },
    { name: "ABSOREX-H-BOX", path: "/products/absorex-h-box" },
    { name: "ABSOREX-BLANKET", path: "/products/absorex-blanket" },
  ];

  return (
    <>
      <section id="topbar" className={`d-flex align-items-center ${!isHeaderVisible ? 'topbar-hidden' : ''}`}>
        <div className="container d-flex justify-content-between align-items-center">
          <div className="contact-info d-flex align-items-center">
            <div className="contact-item">
              <a href={`mailto:${t('topbar.email')}`}>
                <i className="fas fa-envelope"></i>
                <span>{t('topbar.email')}</span>
              </a>
            </div>
            <div className="contact-item">
              <a href={`tel:${t('topbar.phone').replace(/\s/g, '')}`}>
                <i className="fas fa-phone"></i>
                <span>{t('topbar.phone')}</span>
              </a>
            </div>
            <div className="contact-item">
              <button
                type="button"
                onClick={handleBrochureDownload}
                className="brochure-download-btn"
              >
                <i className="fas fa-download"></i>
                <span>{t('topbar.brochure', 'Download Brochure')}</span>
              </button>
            </div>
          </div>
          <div className="social-links d-flex align-items-center">
          </div>
        </div>
      </section>

      <header id="header" className={!isHeaderVisible ? 'header-hidden' : ''}>
        <div className="container d-flex align-items-center justify-content-between">
          <div className="logo">
            <Link to={`${getCurrentLangPrefix()}/`} onClick={scrollToTop}>
              <img src={logo} alt="Company Logo" />
            </Link>
          </div>

          <nav className="navbar">
            <ul>
              <li>
                <NavLink to={`${getCurrentLangPrefix()}/`} end onClick={scrollToTop}>
                  {t('header.home')}
                </NavLink>
              </li>
              <li>
                <NavLink to={`${getCurrentLangPrefix()}/about`} onClick={scrollToTop}>
                  {t('header.about')}
                </NavLink>
              </li>
              <li>
                <NavLink to={`${getCurrentLangPrefix()}/distributor`} onClick={scrollToTop}>
                  {t('header.distributor')}
                </NavLink>
              </li>
              <li className="dropdown">
                <Link to={`${getCurrentLangPrefix()}/products`} onClick={scrollToTop}>
                  {t('header.products')} <i className="fas fa-chevron-down"></i>
                </Link>
                <ul>
                  {productLinks.map((link) => (
                    <li key={link.name}>
                      <Link to={`${getCurrentLangPrefix()}${link.path}`} onClick={scrollToTop}>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <NavLink to={`${getCurrentLangPrefix()}/contact`} onClick={scrollToTop}>
                  {t('header.contact')}
                </NavLink>
              </li>
              <li className="language-dropdown" ref={languageDropdownRef}>
                <button
                  type="button"
                  className="language-toggle"
                  onClick={toggleLanguageDropdown}
                >
                  <div className="language-flag-container">
                    <img src={getCurrentLanguage().flag} alt={getCurrentLanguage().name} />
                    <span>{getCurrentLanguage().code.toUpperCase()}</span>
                    <i className={`fas fa-chevron-${isLanguageDropdownOpen ? 'up' : 'down'}`}></i>
                  </div>
                </button>
                {isLanguageDropdownOpen && (
                  <ul className="language-dropdown-menu">
                    {languages.map((lang) => (
                      <li key={lang.code}>
                        <button
                          type="button"
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`language-option ${lang.code === i18n.language ? 'active' : ''}`}
                        >
                          <img src={lang.flag} alt={lang.name} />
                          <span>{lang.name}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            </ul>
          </nav>

          <button
            type="button"
            className={`mobile-nav-toggle ${isMobileNavOpen ? 'bi-x' : ''}`}
            onClick={toggleMobileNav}
          >
            <i className={isMobileNavOpen ? "fas fa-times" : "fas fa-bars"}></i>
          </button>
        </div>
      </header>

      {isMobileNavOpen && (
        <div className="navbar-mobile">
          <div className="mobile-nav-toggle" onClick={toggleMobileNav}>
            <i className="fas fa-times"></i>
          </div>
          <ul>
            <li>
              <NavLink to={`${getCurrentLangPrefix()}/`} end onClick={handleMobileLinkClick}>
                {t('header.home')}
              </NavLink>
            </li>
            <li>
              <NavLink to={`${getCurrentLangPrefix()}/about`} onClick={handleMobileLinkClick}>
                {t('header.about')}
              </NavLink>
            </li>
            <li>
              <NavLink to={`${getCurrentLangPrefix()}/distributor`} onClick={handleMobileLinkClick}>
                {t('header.distributor')}
              </NavLink>
            </li>
            <li>
              <NavLink to={`${getCurrentLangPrefix()}/products`} onClick={handleMobileLinkClick}>
                {t('header.products')}
              </NavLink>
            </li>
            <li>
              <NavLink to={`${getCurrentLangPrefix()}/contact`} onClick={handleMobileLinkClick}>
                {t('header.contact')}
              </NavLink>
            </li>
            <li>
              <button
                type="button"
                onClick={handleBrochureDownload}
                className="mobile-brochure-btn"
              >
                <i className="fas fa-download"></i>
                <span>{t('topbar.brochure', 'Download Brochure')}</span>
              </button>
            </li>
            <li className="mobile-language-selector">
              <div className="mobile-language-current">
                <img src={getCurrentLanguage().flag} alt={getCurrentLanguage().name} />
                <span>Current: {getCurrentLanguage().name}</span>
              </div>
              {languages.map((lang) => (
                lang.code !== i18n.language && (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => handleLanguageChange(lang.code)}
                    className="mobile-language-toggle"
                  >
                    <img src={lang.flag} alt={lang.name} />
                    <span>Switch to {lang.name}</span>
                  </button>
                )
              ))}
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

export default Header;